import { Box, HStack } from "@chakra-ui/react";
import { ReactNode } from "react";
import { TbChevronDown } from "react-icons/tb";

/**
 * Dropdown — `.dd` split-pill filter selector from the redesign bundle.
 * design ref: styles.css:1717–1753, my-access.jsx:564–586 (CatalogDropdown),
 * bulk-request.jsx (RADropdown).
 *
 * Two stitched sides:
 *   - `.ddl` (left)  — neutral.100 background, holds icon + static label.
 *   - `.ddv` (right) — white background, holds the selected value + chevron.
 *
 * Presentational only — the consumer owns the menu/popover and toggles `open`.
 */

export type DropdownSize = "sm" | "md" | "lg";
export type DropdownTreatment = "outline" | "dropshadow";

export interface DropdownProps {
  /** Icon shown on the label (left) side, before the label text. */
  icon?: ReactNode;
  /** Static text on the label (left) side, after the icon. */
  label?: string;
  /** Selected value text shown on the right side. Null/empty → placeholder. */
  value?: string | null;
  /** Placeholder text when value is empty. */
  placeholder?: string;
  /** Overall height: 32px / 40px / 48px. Default md. */
  size?: DropdownSize;
  /** Border-only ("outline") or shadowed ("dropshadow"). Default outline. */
  treatment?: DropdownTreatment;
  /** Visually-open state — border bumps to neutral.300 and shadow drops. */
  open?: boolean;
  /** Click handler — consumer manages menu state. */
  onClick?: () => void;
  /** Disable interaction & dim. */
  isDisabled?: boolean;
}

const SIZE_TOTAL_HEIGHT: Record<DropdownSize, string> = {
  sm: "32px",
  md: "40px",
  lg: "48px",
};

// Side padding for the inner .ddl / .ddv segments.
// Total height comes from the outer container; each side fills it.
const SIZE_PADDING_X: Record<DropdownSize, string> = {
  sm: "10px",
  md: "12px",
  lg: "14px",
};

const Dropdown = ({
  icon,
  label,
  value,
  placeholder = "Select…",
  size = "md",
  treatment = "outline",
  open = false,
  onClick,
  isDisabled = false,
}: DropdownProps) => {
  const hasValue = value != null && value !== "";
  const px = SIZE_PADDING_X[size];

  // Border & shadow stack — `.open` always wins over treatment.
  const borderColor = open ? "neutral.300" : "neutral.200";
  const boxShadow = open
    ? "none"
    : treatment === "dropshadow"
      ? "elevLow"
      : "none";
  const hoverBoxShadow =
    treatment === "dropshadow" && !open && !isDisabled ? "elevHigh" : undefined;
  const hoverBorderColor =
    treatment === "outline" && !open && !isDisabled ? "neutral.400" : undefined;

  return (
    <Box
      as="button"
      type="button"
      onClick={isDisabled ? undefined : onClick}
      disabled={isDisabled}
      display="inline-flex"
      alignItems="stretch"
      height={SIZE_TOTAL_HEIGHT[size]}
      borderRadius="component.lg" // 8px
      overflow="hidden"
      borderWidth="1px"
      borderStyle="solid"
      borderColor={borderColor}
      boxShadow={boxShadow}
      fontSize="14px"
      lineHeight={1}
      fontFamily="body"
      cursor={isDisabled ? "not-allowed" : "pointer"}
      opacity={isDisabled ? 0.6 : 1}
      transition="box-shadow 120ms, border-color 120ms"
      _hover={{
        boxShadow: hoverBoxShadow,
        borderColor: hoverBorderColor,
      }}
      _focusVisible={{
        outline: "none",
        boxShadow: "focusRing",
      }}
      p={0}
    >
      {/* .ddl — label/icon side */}
      <HStack
        as="span"
        className="ddl"
        spacing="sm" // 8px
        alignItems="center"
        px={px}
        bg="neutral.100"
        color="neutral.700"
        borderRightWidth="1px"
        borderRightStyle="solid"
        borderRightColor="neutral.200"
        height="100%"
        flexShrink={0}
      >
        {icon && (
          <Box
            as="span"
            className="dd-icon"
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            width="16px"
            height="16px"
            flexShrink={0}
          >
            {icon}
          </Box>
        )}
        {label && <Box as="span">{label}</Box>}
      </HStack>

      {/* .ddv — value side */}
      <HStack
        as="span"
        className="ddv"
        spacing="sm" // 8px
        alignItems="center"
        px={px}
        bg="neutral.0"
        height="100%"
        flexGrow={1}
      >
        <Box
          as="span"
          className={hasValue ? "val" : "ph"}
          color={hasValue ? "neutral.950" : "neutral.500"}
          fontWeight={hasValue ? 500 : 400}
        >
          {hasValue ? value : placeholder}
        </Box>
        <Box
          as="span"
          className="chev"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          color="neutral.950"
          opacity={0.65}
          ml="2xs" // 4px — matches the spec's `margin-left: 4px` on `.chev`
        >
          <TbChevronDown size={16} />
        </Box>
      </HStack>
    </Box>
  );
};

export default Dropdown;
